import * as React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import useToastPromise from "../../../hooks/useToast";
import { changePasswordSchema } from "../../../schemas/changePasswordSchema";
import { forgotPassword } from "../../../services/apiAuth";
import { arePasswordRequirementsMet } from "../utils/authUtils";

export function useResetPasswordForm({ uuid, email = "" }) {
  const navigate = useNavigate();
  const { showToastPromise } = useToastPromise();
  const [showPassword, setShowPassword] = React.useState({
    password: false,
    confirmPassword: false,
  });

  const methods = useForm({
    defaultValues: {
      email,
    },
    resolver: yupResolver(changePasswordSchema),
  });
  const { watch, setValue } = methods;
  const password = watch("password") || "";

  const { mutate: resetPassword, isLoading } = useMutation({
    mutationFn: async (data) => showToastPromise(forgotPassword, data),
    onSuccess: () => {
      navigate("/");
    },
    onError: (error) => {
      console.error("An error occurred while resetting password", error);
    },
  });

  React.useEffect(() => {
    if (email) {
      setValue("username", email);
    }
  }, [email, setValue]);

  const togglePasswordVisibility = React.useCallback((field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  }, []);

  const submit = React.useCallback(
    (data) => {
      resetPassword({
        new_password: data.password,
        uuid,
      });
    },
    [resetPassword, uuid]
  );

  const handleFormError = React.useCallback((errors) => {
    console.error(errors);
  }, []);

  return {
    methods,
    showPassword,
    isLoading,
    allRequirementsMet: arePasswordRequirementsMet(password),
    submit,
    togglePasswordVisibility,
    handleFormError,
  };
}
